import React from 'react';
import { Link } from 'react-router-dom';
import {
  catalogLabel,
  catalogOrigin,
} from '../../data/sitePlannerCatalog';
import { FEATURE_KINDS } from '../../services/modelFeatureMap';
import {
  listExteriorCandidates,
  listExteriorEnvOptions,
  listExteriorSkyVariants,
} from '../../data/labExteriorCatalog';
import {
  AMBIENT_PRESETS,
  AMBIENT_TRACKS,
} from '../../data/labAmbientPresets';
import { formatFt } from '../../utils/unitsUs';

function Readout({ k, v, u }) {
  return (
    <div className="readout">
      <div className="k">{k}</div>
      <div className="v">
        {v}
        {u ? <span className="u">{u}</span> : null}
      </div>
    </div>
  );
}

function fmtM(n) {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

const EXTRA_PRESETS = ['approach', 'entry', 'corner'];

/**
 * Sim lab side panel — catalog load, layers, stats, capture preview.
 * Matches DesignSidePanel structure (.side / readouts / chips).
 * Blueprint: docs/SIMULATION_LAB.md · docs/SIM_LAB_ANNOTATE.md
 * · docs/SIM_LAB_SECTION_CUT.md · docs/SIM_LAB_EXTERIOR_ENV.md
 */
export default function SimLabSidePanel({
  catalog,
  slug,
  onSlugChange,
  onLoad,
  layers,
  onToggleLayer,
  /** Horizontal section cut `{ enabled, t }` — docs/SIM_LAB_SECTION_CUT.md */
  sectionCut = { enabled: false, t: 0.85 },
  onSectionCutChange,
  /** Exterior env id — docs/SIM_LAB_EXTERIOR_ENV.md */
  exteriorId = 'none',
  onExteriorIdChange,
  exteriorSkyId = null,
  onExteriorSkyIdChange,
  exteriorLayers = { sky: true, ground: true },
  onExteriorLayersChange,
  /** Ambient audio — docs/SIM_LAB_AMBIENT_AUDIO.md */
  ambientOn = false,
  onAmbientToggle,
  ambientPreset = 'off',
  onAmbientPreset,
  ambientMaster = 80,
  onAmbientMaster,
  ambientTracks = { surf: 0, wind: 0, gulls: 0 },
  onAmbientTrack,
  showLabels,
  onToggleLabels,
  labelsAvailable,
  featureMap,
  featureVisibility,
  onToggleFeature,
  onRemoveDoors,
  onRestoreDoors,
  highlightDoors,
  onToggleHighlightDoors,
  traversePlaying,
  traverseStopLabel,
  onPlayTraverse,
  onStopTraverse,
  stats,
  status,
  shotUrl,
  capturing,
  onCapture,
  onPreset,
  activePreset,
  walkMode = false,
  onToggleWalk,
  annotateMode = false,
  onToggleAnnotate,
  annotations = [],
  annotateKind = 'faucet-hit',
  onAnnotateKind,
  annotateStationId = 'sink-vanity',
  onAnnotateStationId,
  annotateSelectedId = null,
  onAnnotateSelect,
  onAnnotationDelete,
  onAnnotationClear,
  onAnnotationExport,
  onAnnotationNotes,
  annotateSource = 'local',
  authEmail = null,
  stationIds = [],
  annotationKinds = [],
}) {
  const selectedPin = annotations.find((a) => a.id === annotateSelectedId) || null;
  const size = stats?.size;
  const sizeLabel = size
    ? `${fmtM(size[0])}×${fmtM(size[1])}×${fmtM(size[2])}`
    : '—';
  const counts = featureMap?.counts || {};
  const doorCount = counts.door || 0;
  const doorLeaves = featureMap?.doorLeaves ?? doorCount;
  const doorSubitems = featureMap?.doorSubitems ?? 0;
  const doorsHidden = featureVisibility?.door === false;
  const cutT = Math.min(1, Math.max(0, Number(sectionCut?.t) || 0));
  const cutEnabled = Boolean(sectionCut?.enabled);
  const modelHeight = Number(size?.[1]);
  const floorY = Number.isFinite(stats?.floorY) ? stats.floorY : 0;
  const cutY =
    Number.isFinite(modelHeight) && modelHeight > 0
      ? floorY + cutT * modelHeight
      : null;
  const exteriorOptions = listExteriorEnvOptions();
  const exteriorCandidates = listExteriorCandidates();
  const skyVariants = listExteriorSkyVariants(exteriorId);
  const activeSkyId = exteriorSkyId || skyVariants[0]?.id || null;

  return (
    <aside className="side">
      <div className="side-head">
        <div className="side-h">internal · sim lab</div>
        <div className="side-title">Localization lab</div>
        <div className="side-sub">
          Load a catalog GLB, frame with localize-v0 presets, capture screenshots.
          {stats?.slug ? ` · ${catalogLabel(stats.slug)}` : ''}
        </div>
      </div>

      <div className="side-body">
        <div className="side-section">
          <div className="readouts">
            <Readout k="model" v={stats?.slug ? catalogLabel(stats.slug) : '—'} />
            <Readout k="localize" v={stats?.version || '—'} />
            <Readout
              k="front"
              v={
                stats?.frontAxis
                  ? `${stats.frontAxis}${
                      stats.frontSource === 'doors' ? ' · door' : ''
                    }`
                  : '—'
              }
            />
            <Readout k="size" v={sizeLabel} u="m" />
          </div>
          {stats?.floorY != null || stats?.entry ? (
            <p
              style={{
                margin: '8px 0 0',
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                color: 'var(--mid)',
                lineHeight: 1.45,
              }}
            >
              floor Y {Number.isFinite(stats.floorY) ? stats.floorY.toFixed(2) : '0'}
              {' · '}
              entry on {stats.frontAxis || '+z'}
              {stats.frontSource === 'doors'
                ? ' (from exterior door)'
                : ' (default +z until doors)'}
              {' · '}
              entry · floor ring = footprint
            </p>
          ) : null}
        </div>

        <div className="side-section" id="sim-lab-catalog">
          <div className="sec-h">catalog</div>
          <div className="field">
            <label className="field-label" htmlFor="sim-lab-model">
              Model
            </label>
            <select
              id="sim-lab-model"
              value={slug}
              onChange={(e) => onSlugChange(e.target.value)}
              aria-label="Catalog model"
              style={{
                width: '100%',
                padding: '10px 10px',
                minHeight: 44,
                fontSize: 12,
                fontFamily: 'var(--font-mono)',
                border: '1px solid var(--line)',
                background: 'var(--panel)',
                color: 'var(--ink)',
                borderRadius: 0,
              }}
            >
              <optgroup label="Construx">
                {catalog
                  .filter((m) => m.origin === 'construx')
                  .map((m) => (
                    <option key={m.slug} value={m.slug}>
                      {m.label}
                    </option>
                  ))}
              </optgroup>
              <optgroup label="SoftPlan examples">
                {catalog
                  .filter((m) => m.origin === 'softplan')
                  .map((m) => (
                    <option key={m.slug} value={m.slug}>
                      {catalogOrigin(m.slug) === 'softplan' ? 'SP · ' : ''}
                      {catalogLabel(m.slug)}
                    </option>
                  ))}
              </optgroup>
            </select>
          </div>
          <button
            type="button"
            className="button-primary"
            style={{ width: '100%', minHeight: 44, marginTop: 10 }}
            onClick={onLoad}
          >
            Load model
          </button>
        </div>

        <div className="side-section">
          <div className="sec-h">camera · walk</div>
          <div className="catalog-chip-row" role="list">
            {EXTRA_PRESETS.map((key) => (
              <button
                key={key}
                type="button"
                role="listitem"
                className={`chip${
                  !walkMode && activePreset === key ? ' sel' : ''
                }`}
                disabled={walkMode}
                onClick={() => onPreset(key)}
              >
                {key}
              </button>
            ))}
            <button
              type="button"
              role="listitem"
              className={`chip${walkMode ? ' sel' : ''}`}
              aria-pressed={walkMode}
              title="Walk · 6 ft POV (G)"
              onClick={onToggleWalk}
            >
              walk · 6ft
            </button>
            <button
              type="button"
              role="listitem"
              className={`chip${annotateMode ? ' sel' : ''}`}
              aria-pressed={annotateMode}
              title="Annotate pins (N)"
              onClick={onToggleAnnotate}
            >
              annotate
            </button>
          </div>
          <p
            style={{
              margin: '8px 0 0',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
              lineHeight: 1.45,
            }}
          >
            {annotateMode
              ? 'Click scene to pin · set kind/station below · N exit'
              : walkMode
                ? 'WASD · Q/E · drag look · N annotate · Esc exit'
                : 'Rail: I/F/S/T · Walk G · Annotate N.'}
          </p>
        </div>

        <div className="side-section" id="sim-lab-annotate">
          <div className="sec-h">annotate · pins</div>
          <p
            style={{
              margin: '0 0 8px',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
              lineHeight: 1.45,
            }}
          >
            store · <code>{annotateSource}</code>
            {authEmail ? (
              <>
                {' '}
                · <code>{authEmail}</code>
              </>
            ) : null}
          </p>
          <div className="field" style={{ marginBottom: 8 }}>
            <label className="field-label" htmlFor="ann-kind">
              Kind (next pin)
            </label>
            <select
              id="ann-kind"
              value={annotateKind}
              onChange={(e) => onAnnotateKind?.(e.target.value)}
              style={{
                width: '100%',
                minHeight: 40,
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                border: '1px solid var(--line)',
                background: 'var(--panel)',
                color: 'var(--ink)',
              }}
            >
              {annotationKinds.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 8 }}>
            <label className="field-label" htmlFor="ann-station">
              Station
            </label>
            <select
              id="ann-station"
              value={annotateStationId}
              onChange={(e) => onAnnotateStationId?.(e.target.value)}
              style={{
                width: '100%',
                minHeight: 40,
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                border: '1px solid var(--line)',
                background: 'var(--panel)',
                color: 'var(--ink)',
              }}
            >
              {stationIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </div>
          <p
            style={{
              margin: '0 0 8px',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
              lineHeight: 1.45,
            }}
          >
            Vanity: <code>faucet-hit</code> + <code>faucet-spout</code> ·{' '}
            <code>sink-vanity</code>. Stove: <code>stove-display</code> to nudge
            the clock. {annotations.length} pin
            {annotations.length === 1 ? '' : 's'}. Select a pin to edit notes.
          </p>
          <ul className="lab-ann-list">
            {annotations.length === 0 ? (
              <li className="lab-ann-empty">No pins yet.</li>
            ) : (
              annotations.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className={`lab-ann-row${
                      annotateSelectedId === a.id ? ' sel' : ''
                    }`}
                    onClick={() => onAnnotateSelect?.(a.id)}
                  >
                    <span className="lab-ann-row-k">{a.kind}</span>
                    <span className="lab-ann-row-s">{a.stationId}</span>
                    {a.notes ? (
                      <span className="lab-ann-row-n" title={a.notes}>
                        note
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className="lab-ann-del"
                    title="Delete pin"
                    onClick={() => onAnnotationDelete?.(a.id)}
                  >
                    ×
                  </button>
                </li>
              ))
            )}
          </ul>
          {selectedPin ? (
            <div className="field lab-ann-notes" style={{ marginTop: 10 }}>
              <label className="field-label" htmlFor="ann-notes">
                Notes
                {selectedPin.userEmail ? (
                  <span className="lab-ann-notes-by">
                    {' '}
                    · {selectedPin.userEmail}
                  </span>
                ) : null}
              </label>
              <textarea
                id="ann-notes"
                key={selectedPin.id}
                defaultValue={selectedPin.notes || ''}
                rows={3}
                placeholder="Context for this pin…"
                onBlur={(e) => {
                  const next = e.target.value;
                  if (next !== (selectedPin.notes || '')) {
                    onAnnotationNotes?.(selectedPin.id, next);
                  }
                }}
              />
            </div>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="button-secondary"
              style={{ flex: '1 1 100px', minHeight: 40 }}
              onClick={onAnnotationExport}
              disabled={!annotations.length}
            >
              Export JSON
            </button>
            <button
              type="button"
              className="button-secondary"
              style={{ flex: '1 1 80px', minHeight: 40 }}
              onClick={onAnnotationClear}
              disabled={!annotations.length}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="side-section">
          <div className="sec-h">layers</div>
          <div className="catalog-chip-row" role="list">
            {['roof', 'walls', 'interior'].map((key) => (
              <button
                key={key}
                type="button"
                role="listitem"
                className={`chip${layers[key] ? ' sel' : ''}`}
                aria-pressed={Boolean(layers[key])}
                onClick={() => onToggleLayer(key)}
              >
                {key}
              </button>
            ))}
            <button
              type="button"
              role="listitem"
              className={`chip${showLabels ? ' sel' : ''}`}
              aria-pressed={showLabels}
              disabled={!labelsAvailable}
              title={
                labelsAvailable
                  ? 'Toggle room labels'
                  : 'Room labels only on R1AS'
              }
              onClick={onToggleLabels}
            >
              labels
            </button>
          </div>
        </div>

        <div className="side-section" id="sim-lab-section-cut">
          <div className="sec-h">section cut</div>
          <div className="catalog-chip-row" role="list">
            <button
              type="button"
              role="listitem"
              className={`chip${cutEnabled ? ' sel' : ''}`}
              aria-pressed={cutEnabled}
              title="Horizontal cross-section — clip above height"
              onClick={() =>
                onSectionCutChange?.({
                  enabled: !cutEnabled,
                  t: cutT,
                })
              }
            >
              horizontal
            </button>
          </div>
          <label
            style={{
              display: 'block',
              marginTop: 10,
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
            }}
          >
            Height ·{' '}
            {cutY != null ? formatFt(cutY) : '—'}
            {Number.isFinite(modelHeight)
              ? ` / ${formatFt(floorY + modelHeight)}`
              : ''}
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              disabled={!cutEnabled}
              value={Math.round(cutT * 100)}
              aria-label="Horizontal section cut height"
              onChange={(e) =>
                onSectionCutChange?.({
                  enabled: true,
                  t: Number(e.target.value) / 100,
                })
              }
              style={{
                display: 'block',
                width: '100%',
                marginTop: 6,
                opacity: cutEnabled ? 1 : 0.4,
              }}
            />
          </label>
          <p
            style={{
              margin: '6px 0 0',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
              lineHeight: 1.45,
            }}
          >
            Peels geometry above the plane. Works in Walk and orbit.
          </p>
        </div>

        <div className="side-section" id="sim-lab-exterior">
          <div className="sec-h">exterior</div>
          <p
            style={{
              margin: '0 0 8px',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
              lineHeight: 1.45,
            }}
          >
            Transport the ADU into a place. The HDRI photo is projected onto
            the ground (sea + horizon included); a sand apron seats the model.
          </p>
          <div className="catalog-chip-row" role="list">
            {exteriorOptions.map((opt) => {
              const selected = exteriorId === opt.id;
              const locked = !opt.ready && opt.id !== 'none';
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="listitem"
                  className={`chip${selected ? ' sel' : ''}`}
                  aria-pressed={selected}
                  title={
                    locked
                      ? `${opt.label} — pack assets first (see candidates)`
                      : opt.description
                  }
                  disabled={locked}
                  onClick={() => onExteriorIdChange?.(opt.id)}
                >
                  {opt.label}
                  {locked ? ' · pack' : ''}
                </button>
              );
            })}
          </div>
          {exteriorId && exteriorId !== 'none' && skyVariants.length > 1 ? (
            <>
              <div
                style={{
                  margin: '10px 0 4px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--mid)',
                }}
              >
                sky variant
              </div>
              <div className="catalog-chip-row" role="list">
                {skyVariants.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    role="listitem"
                    className={`chip${activeSkyId === v.id ? ' sel' : ''}`}
                    aria-pressed={activeSkyId === v.id}
                    title={v.attribution}
                    onClick={() => onExteriorSkyIdChange?.(v.id)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          {exteriorId && exteriorId !== 'none' ? (
            <div className="catalog-chip-row" role="list" style={{ marginTop: 8 }}>
              {['sky', 'ground'].map((key) => (
                <button
                  key={key}
                  type="button"
                  role="listitem"
                  className={`chip${exteriorLayers?.[key] !== false ? ' sel' : ''}`}
                  aria-pressed={exteriorLayers?.[key] !== false}
                  onClick={() =>
                    onExteriorLayersChange?.({
                      ...exteriorLayers,
                      [key]: exteriorLayers?.[key] === false,
                    })
                  }
                >
                  {key}
                </button>
              ))}
            </div>
          ) : null}
          {exteriorCandidates.length > 0 ? (
            <details style={{ marginTop: 10 }}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--ink-2)',
                }}
              >
                Asset candidates ({exteriorCandidates.length}) — CC0 shortlist
              </summary>
              <ul
                style={{
                  margin: '8px 0 0',
                  paddingLeft: 18,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  color: 'var(--mid)',
                  lineHeight: 1.5,
                }}
              >
                {exteriorCandidates.map((a) => (
                  <li key={a.id} style={{ marginBottom: 4 }}>
                    <strong style={{ color: 'var(--ink-2)' }}>{a.id}</strong>
                    {' · '}
                    {a.role}
                    {' · '}
                    <a
                      href={a.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--accent, #2b4eff)' }}
                    >
                      source
                    </a>
                    {a.notes ? ` — ${a.notes}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>

        <div className="side-section" id="sim-lab-audio">
          <div className="sec-h">audio · ambient</div>
          <div className="catalog-chip-row" role="list">
            <button
              type="button"
              role="listitem"
              className={`chip${ambientOn ? ' sel' : ''}`}
              aria-pressed={ambientOn}
              title="Toggle ambient soundscape"
              onClick={onAmbientToggle}
            >
              {ambientOn ? 'sound on' : 'sound off'}
            </button>
            {AMBIENT_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="listitem"
                className={`chip${ambientPreset === p.id ? ' sel' : ''}`}
                aria-pressed={ambientPreset === p.id}
                disabled={!ambientOn}
                onClick={() => onAmbientPreset?.(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <label
            style={{
              display: 'block',
              marginTop: 10,
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
            }}
          >
            master · {ambientMaster}
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              disabled={!ambientOn}
              value={ambientMaster}
              aria-label="Ambient master volume"
              onChange={(e) => onAmbientMaster?.(Number(e.target.value))}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                opacity: ambientOn ? 1 : 0.4,
              }}
            />
          </label>
          {AMBIENT_TRACKS.map((t) => (
            <label
              key={t.id}
              style={{
                display: 'block',
                marginTop: 8,
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                color: 'var(--mid)',
              }}
              title={t.description}
            >
              {t.label} · {ambientTracks?.[t.id] ?? 0}
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                disabled={!ambientOn}
                value={ambientTracks?.[t.id] ?? 0}
                aria-label={`${t.label} volume`}
                onChange={(e) =>
                  onAmbientTrack?.(t.id, Number(e.target.value))
                }
                style={{
                  display: 'block',
                  width: '100%',
                  marginTop: 4,
                  opacity: ambientOn ? 1 : 0.4,
                }}
              />
            </label>
          ))}
          <p
            style={{
              margin: '8px 0 0',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
              lineHeight: 1.45,
            }}
          >
            Procedural surf · wind · gulls (no audio files). Walking indoors
            muffles the mix.
          </p>
        </div>

        <div className="side-section" id="sim-lab-features">
          <div className="sec-h">features · map</div>
          <div className="catalog-chip-row" role="list">
            {FEATURE_KINDS.map((kind) => {
              const n = counts[kind] || 0;
              const on = featureVisibility?.[kind] !== false;
              return (
                <button
                  key={kind}
                  type="button"
                  role="listitem"
                  className={`chip${on ? ' sel' : ''}`}
                  aria-pressed={on}
                  disabled={!featureMap}
                  title={`${kind}: ${n} mesh${n === 1 ? '' : 'es'}`}
                  onClick={() => onToggleFeature?.(kind)}
                >
                  {kind} {n}
                </button>
              );
            })}
          </div>
          <p
            style={{
              margin: '8px 0 0',
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              color: 'var(--mid)',
              lineHeight: 1.45,
            }}
          >
            SoftPlan submeshes · doors / appliances via bbox heuristic (no name
            tags).{' '}
            {doorLeaves} door famil{doorLeaves === 1 ? 'y' : 'ies'}
            {doorSubitems
              ? ` · ${doorSubitems} panel/handle subitem${
                  doorSubitems === 1 ? '' : 's'
                }`
              : ''}
            {' · '}
            {doorCount} mesh{doorCount === 1 ? '' : 'es'} total.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              className="button-primary"
              style={{ flex: '1 1 120px', minHeight: 44 }}
              disabled={!featureMap || doorCount === 0}
              onClick={doorsHidden ? onRestoreDoors : onRemoveDoors}
            >
              {doorsHidden ? 'Restore doors' : 'Remove doors'}
            </button>
            <button
              type="button"
              className={`button-secondary${highlightDoors ? ' is-on' : ''}`}
              style={{ flex: '1 1 120px', minHeight: 44 }}
              disabled={!featureMap || doorCount === 0}
              aria-pressed={highlightDoors}
              onClick={onToggleHighlightDoors}
            >
              {highlightDoors ? 'Highlight on' : 'Highlight doors'}
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="button-secondary"
              style={{ flex: '1 1 140px', minHeight: 44 }}
              disabled={!featureMap || traversePlaying}
              onClick={onPlayTraverse}
            >
              {traversePlaying
                ? `Playing · ${traverseStopLabel || '…'}`
                : 'Play in → out'}
            </button>
            <button
              type="button"
              className="button-secondary"
              style={{ flex: '0 0 auto', minHeight: 44, minWidth: 72 }}
              disabled={!traversePlaying}
              onClick={onStopTraverse}
            >
              Stop
            </button>
          </div>
        </div>

        <div className="side-section">
          <div className="sec-h">capture</div>
          <button
            type="button"
            className="button-secondary"
            style={{ width: '100%', minHeight: 44 }}
            onClick={onCapture}
            disabled={capturing}
          >
            {capturing ? 'Capturing…' : 'Screenshot'}
          </button>
          {shotUrl ? (
            <div
              className="sim-lab-shot"
              style={{
                marginTop: 10,
                border: '1px solid var(--line)',
                overflow: 'hidden',
                background: '#111',
              }}
            >
              <img
                src={shotUrl}
                alt="Last lab screenshot"
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </div>
          ) : (
            <p
              style={{
                margin: '8px 0 0',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--mid)',
              }}
            >
              No capture yet.
            </p>
          )}
          {status ? (
            <p
              style={{
                margin: '8px 0 0',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-2)',
              }}
            >
              {status}
            </p>
          ) : null}
        </div>

        <div className="side-section">
          <div className="sec-h">next</div>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              color: 'var(--ink-2)',
              lineHeight: 1.45,
            }}
          >
            Feature map · doors · traverse · walk 6ft · horizontal section
            cut. R1AS tip: click a faucet or shower valve for blue water.
          </p>
        </div>

        <div
          className="side-section"
          style={{ borderBottom: 0, display: 'flex', gap: 8 }}
        >
          <Link
            className="button-secondary"
            to="/internal"
            style={{ flex: 1, textAlign: 'center', minHeight: 44 }}
          >
            ← internal
          </Link>
          <Link
            className="button-primary"
            to="/xlabs/design"
            style={{ flex: 1, textAlign: 'center', minHeight: 44 }}
          >
            design →
          </Link>
        </div>
      </div>
    </aside>
  );
}
